import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Plus } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { admin } from '@/lib/api';

const COUPON_STATUS_CLASS = {
  issued: 'bg-emerald-600 text-white',
  redeemed: 'bg-indigo-600 text-white',
  revoked: 'bg-slate-600 text-white',
  expired: 'bg-amber-500 text-slate-900',
};

const TRIAL_STATUS_CLASS = {
  active: 'bg-emerald-600 text-white',
  converted: 'bg-indigo-600 text-white',
  expired: 'bg-slate-600 text-white',
};

function formatDay(value) {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch (_e) {
    return String(value);
  }
}

function errorText(error, fallback) {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
}

// Subscription Manager tooling for Trial Premium (KAN-136 FR-8): coupon management, the trial
// accounts list, and the issued -> redeemed -> converted funnel.
export default function TrialPremiumTab() {
  const [coupons, setCoupons] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [isIssuing, setIsIssuing] = useState(false);
  const [couponToRevoke, setCouponToRevoke] = useState(null);
  const [accountToEnd, setAccountToEnd] = useState(null);
  const [isActing, setIsActing] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [couponsRes, accountsRes, funnelRes] = await Promise.all([
        admin.listTrialCoupons(),
        admin.listTrialAccounts(),
        admin.getTrialFunnel(),
      ]);
      setCoupons(couponsRes.data.coupons || []);
      setAccounts(accountsRes.data.accounts || []);
      setFunnel(funnelRes.data);
    } catch (_e) {
      toast.error('Failed to load Trial Premium data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleIssue = async (event) => {
    event.preventDefault();
    if (!email.trim()) return;
    setIsIssuing(true);
    try {
      const res = await admin.issueTrialCoupon(email.trim());
      toast.success(`Coupon ${res.data.code} issued to ${res.data.email}`);
      setEmail('');
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Failed to issue coupon'));
    } finally {
      setIsIssuing(false);
    }
  };

  const handleRevoke = async () => {
    setIsActing(true);
    try {
      await admin.revokeTrialCoupon(couponToRevoke.code);
      toast.success(`Revoked ${couponToRevoke.code}`);
      setCouponToRevoke(null);
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Failed to revoke coupon'));
    } finally {
      setIsActing(false);
    }
  };

  const handleEndTrial = async () => {
    setIsActing(true);
    try {
      await admin.endTrialNow(accountToEnd.employer_id);
      toast.success(`Trial ended for ${accountToEnd.email || accountToEnd.employer_id}`);
      setAccountToEnd(null);
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Failed to end trial'));
    } finally {
      setIsActing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="trial-premium-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3" data-testid="trial-funnel">
          {[
            ['Issued', funnel?.issued],
            ['Redeemed', funnel?.redeemed],
            ['Converted', funnel?.converted],
          ].map(([label, value]) => (
            <Card key={label} className="bg-slate-800 border-slate-700 px-5 py-3">
              <p className="text-xs text-slate-400">{label}</p>
              <p className="text-2xl font-bold text-white" data-testid={`trial-funnel-${label.toLowerCase()}`}>
                {value ?? '—'}
              </p>
            </Card>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
          data-testid="trial-refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Coupons */}
      <Card className="bg-slate-800 border-slate-700 p-4 space-y-4">
        <h3 className="text-white font-semibold">Coupons</h3>
        <form onSubmit={handleIssue} className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            placeholder="Prospect email the coupon is bound to"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="sm:max-w-sm bg-slate-900 border-slate-700 text-white placeholder:text-slate-400"
            data-testid="trial-coupon-email-input"
          />
          <Button
            type="submit"
            disabled={isIssuing || !email.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="trial-coupon-issue-btn"
          >
            {isIssuing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Generate coupon
          </Button>
        </form>

        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : coupons.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="trial-coupons-empty">No coupons issued yet.</p>
        ) : (
          <ScrollArea className="h-[300px]">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Code</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Issued</th>
                  <th className="px-3 py-2 font-medium">Expires</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700 text-slate-200">
                {coupons.map((coupon) => (
                  <tr key={coupon.code} data-testid={`trial-coupon-row-${coupon.code}`}>
                    <td className="px-3 py-2 font-mono">{coupon.code}</td>
                    <td className="px-3 py-2">{coupon.email}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${COUPON_STATUS_CLASS[coupon.status] || 'bg-slate-600 text-white'} font-normal`}>
                        {coupon.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{formatDay(coupon.issued_at)}</td>
                    <td className="px-3 py-2">{formatDay(coupon.expires_at)}</td>
                    <td className="px-3 py-2 text-right">
                      {coupon.status === 'issued' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-400 hover:text-rose-300"
                          onClick={() => setCouponToRevoke(coupon)}
                          data-testid={`trial-coupon-revoke-${coupon.code}`}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </Card>

      {/* Trial accounts */}
      <Card className="bg-slate-800 border-slate-700 p-4 space-y-4">
        <h3 className="text-white font-semibold">Trial accounts</h3>
        {isLoading ? null : accounts.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="trial-accounts-empty">No accounts have started a trial yet.</p>
        ) : (
          <ScrollArea className="h-[300px]">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Account</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Started</th>
                  <th className="px-3 py-2 font-medium">Ends</th>
                  <th className="px-3 py-2 font-medium text-center">Schools</th>
                  <th className="px-3 py-2 font-medium text-center">Subs</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700 text-slate-200">
                {accounts.map((account) => (
                  <tr key={account.employer_id} data-testid={`trial-account-row-${account.employer_id}`}>
                    <td className="px-3 py-2">
                      <div>{account.name || '—'}</div>
                      <div className="text-xs text-slate-400">{account.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`${TRIAL_STATUS_CLASS[account.status] || 'bg-slate-600 text-white'} font-normal`}>
                        {account.status}
                      </Badge>
                      {account.scheduled_plan_id && (
                        <div className="text-xs text-slate-400 mt-1">→ {account.scheduled_plan_id}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">{formatDay(account.trial_started_at)}</td>
                    <td className="px-3 py-2">{formatDay(account.trial_ends_at)}</td>
                    <td className="px-3 py-2 text-center">{account.active_schools}</td>
                    <td className="px-3 py-2 text-center">{account.active_subs}</td>
                    <td className="px-3 py-2 text-right">
                      {account.status === 'active' && !account.scheduled_plan_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-400 hover:text-rose-300"
                          onClick={() => setAccountToEnd(account)}
                          data-testid={`trial-end-now-${account.employer_id}`}
                        >
                          End trial now
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </Card>

      <AlertDialog open={!!couponToRevoke} onOpenChange={(open) => !open && setCouponToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this coupon?</AlertDialogTitle>
            <AlertDialogDescription>
              {couponToRevoke?.code} ({couponToRevoke?.email}) can no longer be redeemed. You can issue a new coupon
              for that email afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActing}>Keep coupon</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} disabled={isActing} data-testid="trial-coupon-confirm-revoke-btn">
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!accountToEnd} onOpenChange={(open) => !open && setAccountToEnd(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End this trial now?</AlertDialogTitle>
            <AlertDialogDescription>
              {accountToEnd?.email || accountToEnd?.employer_id} moves to Head Start immediately, with the same
              archival rules as a normal expiry (most recent schools, Subs and co-admins beyond the free limits are
              archived, never deleted). The account cannot start another trial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActing}>Keep trial</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndTrial} disabled={isActing} data-testid="trial-confirm-end-btn">
              End trial
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
